import React from "react";
import {
  Check,
  X,
  Clock,
  Calendar,
  CreditCard,
  RefreshCw,
  MapPin,
  User,
  MessageCircle,
  UserPlus,
  Bell,
} from "lucide-react";

const NotificationItem = ({
  notification,
  onMarkAsRead,
  onDelete,
  onAccept,
  onReject,
  onClickItem,
}) => {
  const normType = (notification.type || '').toLowerCase();

  const getIcon = (type) => {
    const iconProps = { className: "w-5 h-5" };
    switch (type) {
      case "companion_accepted":
      case "trip_accepted":
        return <UserPlus {...iconProps} />;
      case "change_request_created":
        return <RefreshCw {...iconProps} />;
      case "change_request_merged":
        return <Check {...iconProps} />;
      case "trip_published":
        return <MapPin {...iconProps} />;
      case "buddy_request_received":
        return <UserPlus {...iconProps} />;
      case "buddy_request_accepted":
        return <User {...iconProps} />;
      case "new_message":
        return <MessageCircle {...iconProps} />;
      case "trip_invitation":
        return <MapPin {...iconProps} />;
      default:
        return <Bell {...iconProps} />;
    }
  };

  const getColorClasses = (type, isRead) => {
    const opacity = isRead ? "bg-opacity-50" : "bg-opacity-100";
    const colors = {
      companion_accepted: `bg-emerald-100 ${opacity} text-emerald-700`,
      trip_accepted: `bg-teal-100 ${opacity} text-teal-600`,
      change_request_created: `bg-amber-100 ${opacity} text-amber-700`,
      change_request_merged: `bg-cyan-100 ${opacity} text-cyan-700`,
      trip_published: `bg-teal-100 ${opacity} text-teal-600`,
      buddy_request_received: `bg-purple-100 ${opacity} text-purple-600`,
      buddy_request_accepted: `bg-green-100 ${opacity} text-green-600`,
      new_message: `bg-blue-100 ${opacity} text-blue-600`,
      trip_invitation: `bg-orange-100 ${opacity} text-orange-600`,
    };
    return colors[type] || `bg-teal-100 ${opacity} text-teal-600`;
  };

  const formatTime = (date) => {
    if (!date) return "Just now";
    try {
      const now = new Date();
      const notifDate = new Date(date);
      if (isNaN(notifDate.getTime())) return "Just now";

      const diffMs = now - notifDate;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) return "Just now";
      if (diffMins < 60) return `${diffMins} min ago`;
      if (diffHours < 24)
        return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
      return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
    } catch (error) {
      return "Just now";
    }
  };

  const isBuddyRequest = notification.type === "buddy_request_received";
  const isMessageNotification = notification.type === "new_message";

  return (
    <div
      onClick={() => onClickItem && onClickItem(notification)}
      className={`p-4 hover:bg-gray-50 transition-colors duration-150 group cursor-pointer ${!notification.isRead ? "bg-teal-50/40" : ""
        }`}
    >
      <div className="flex gap-3">
        <div
          className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${isMessageNotification && notification.from?.avatarUrl
              ? ""
              : getColorClasses(normType, notification.isRead)
            }`}
        >
          {isMessageNotification && notification.from?.avatarUrl ? (
            <img
              src={notification.from.avatarUrl}
              alt={notification.from.name}
              className="w-10 h-10 rounded-full object-cover"
            />
          ) : (
            getIcon(normType)
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-1">
            <h4
              className={`font-semibold text-sm leading-tight break-words ${notification.isRead ? "text-gray-700" : "text-gray-900"
                }`}
            >
              {notification.title}
            </h4>
            {!notification.isRead && (
              <div className="w-2 h-2 bg-teal-500 rounded-full flex-shrink-0 mt-1 animate-pulse"></div>
            )}
          </div>

          <p
            className={`text-sm mb-2 leading-relaxed line-clamp-2 ${notification.isRead ? "text-gray-500" : "text-gray-600"
              }`}
          >
            {notification.message}
          </p>

          {isBuddyRequest && notification.relatedId && (
            <div className="flex gap-2 mb-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onAccept(notification.relatedId, notification.id || notification._id);
                }}
                className="flex items-center gap-1 px-3 py-1.5 bg-green-500 hover:bg-green-600 text-white text-xs font-medium rounded-lg transition-colors"
              >
                <Check className="w-3.5 h-3.5" />
                Accept
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onReject(notification.relatedId, notification.id || notification._id);
                }}
                className="flex items-center gap-1 px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white text-xs font-medium rounded-lg transition-colors"
              >
                <X className="w-3.5 h-3.5" />
                Reject
              </button>
            </div>
          )}

          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-400 flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {formatTime(notification.createdAt)}
            </span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete(notification.id || notification._id);
              }}
              className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 text-gray-400 hover:text-red-500 p-1 hover:bg-red-50 rounded-lg"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationItem;
